import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  FaCloud,
  FaServer,
  FaDatabase,
  FaHdd,
  FaNetworkWired,
  FaShieldAlt,
  FaChartPie,
  FaCopy,
  FaCheck,
  FaDownload,
  FaExclamationTriangle,
  FaLightbulb,
  FaBolt,
  FaSlidersH,
  FaCode,
  FaGlobe,
  FaBookOpen,
  FaChevronDown,
  FaChevronUp,
  FaInfoCircle
} from 'react-icons/fa';

import {
  CURRENCIES,
  AWS_REGIONS,
  EC2_INSTANCES,
  RDS_ENGINES,
  RDS_INSTANCE_CLASSES,
  FINOPS_SAVINGS_RATIOS,
  ARCHITECTURE_PRESETS,
  calculateCloudCosts
} from '../utils/awsPricingData';

import { generateTerraformHcl } from '../utils/terraformGenerator';

const CloudCostEstimatorPage = () => {
  // Navigation & View State
  const [activeTab, setActiveTab] = useState('sizer'); // 'sizer', 'finops', 'topology', 'terraform'
  const [currencyKey, setCurrencyKey] = useState('USD');
  const [copiedCode, setCopiedCode] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [showBestPractices, setShowBestPractices] = useState(false);

  // Active Architecture Configuration (default: High-Availability 3-Tier Enterprise)
  const [config, setConfig] = useState(() => ARCHITECTURE_PRESETS[1].config);
  const [activePresetId, setActivePresetId] = useState('ha_enterprise_3tier');

  // Currency helper
  const currency = CURRENCIES[currencyKey] || CURRENCIES.USD;
  const formatCost = (usdAmount) => {
    const converted = usdAmount * currency.rate;
    return `${currency.symbol}${converted.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`;
  };

  // Cost Calculation
  const costReport = useMemo(() => calculateCloudCosts(config), [config]);

  // Apply Architectural Preset
  const handleApplyPreset = (preset) => {
    setActivePresetId(preset.id);
    setConfig({ ...preset.config });
  };

  // Partial State Updaters
  const updateNested = (category, field, value) => {
    setConfig(prev => ({
      ...prev,
      [category]: {
        ...prev[category],
        [field]: value
      }
    }));
  };

  // Copy Terraform code
  const handleCopyTerraform = () => {
    const code = generateTerraformHcl(config);
    navigator.clipboard.writeText(code);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  // Download Terraform .tf file
  const handleDownloadTerraform = () => {
    const code = generateTerraformHcl(config);
    const blob = new Blob([code], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `aws_architecture_${config.region}.tf`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: 'sizer', label: 'Architecture Sizer & BOM', icon: <FaSlidersH /> },
    { id: 'finops', label: 'FinOps & Savings Plans', icon: <FaShieldAlt /> },
    { id: 'topology', label: 'Topology & Burst Simulator', icon: <FaBolt /> },
    { id: 'terraform', label: 'Terraform IaC Generator', icon: <FaCode /> }
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#040d1a',
      color: '#e2e8f0',
      padding: '30px 20px 80px 20px',
      fontFamily: "'Segoe UI', Roboto, Helvetica, Arial, sans-serif"
    }}>
      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        
        {/* Breadcrumb & Navigation */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d2d3', fontSize: '0.88rem', fontWeight: 600 }}>
            <Link to="/tools" style={{ color: '#94a3b8', textDecoration: 'none' }}>Tools Hub</Link>
            <span style={{ color: '#64748b' }}>/</span>
            <span style={{ color: '#00d2d3' }}>Cloud Architecture</span>
          </div>

          {/* Currency & Region Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '8px', padding: '4px 10px' }}>
              <FaGlobe size={14} color="#00d2d3" />
              <select
                value={config.region}
                onChange={(e) => setConfig(prev => ({ ...prev, region: e.target.value }))}
                style={{
                  background: 'transparent',
                  color: '#e2e8f0',
                  border: 'none',
                  outline: 'none',
                  fontSize: '0.82rem',
                  cursor: 'pointer'
                }}
              >
                {AWS_REGIONS.map(r => (
                  <option key={r.id} value={r.id} style={{ background: '#0a192f', color: '#fff' }}>
                    {r.name}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '8px', padding: '3px' }}>
              {Object.keys(CURRENCIES).map(key => (
                <button
                  key={key}
                  onClick={() => setCurrencyKey(key)}
                  style={{
                    background: currencyKey === key ? '#00d2d3' : 'transparent',
                    color: currencyKey === key ? '#040d1a' : '#94a3b8',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '3px 8px',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {key}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Hero Banner with Live Total Sizing */}
        <div style={{
          background: 'linear-gradient(135deg, rgba(8, 28, 54, 0.85) 0%, rgba(4, 15, 30, 0.95) 100%)',
          border: '1px solid rgba(0, 210, 211, 0.35)',
          borderRadius: '16px',
          padding: '24px 28px',
          marginBottom: '28px',
          boxShadow: '0 12px 30px rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '20px'
        }}>
          <div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', background: 'rgba(0, 210, 211, 0.15)', border: '1px solid rgba(0, 210, 211, 0.35)', padding: '4px 12px', borderRadius: '20px', fontSize: '0.78rem', color: '#00d2d3', fontWeight: 700, marginBottom: '8px' }}>
              <FaCloud size={13} />
              <span>AWS Cloud Architect & FinOps Engineering Suite</span>
            </div>
            <h1 style={{ fontSize: '2.1rem', color: '#ffffff', margin: '0 0 8px 0', fontWeight: 700, letterSpacing: '-0.5px' }}>
              AWS & Multi-Cloud Infrastructure Estimator
            </h1>
            <p style={{ fontSize: '0.92rem', color: '#94a3b8', margin: '0 0 16px 0', maxWidth: '720px', lineHeight: '1.5' }}>
              Interactive workload sizing, monthly burst modeling, and reserved instance cost optimization for EC2, S3, RDS, ALB, and VPC NAT gateways with instant Terraform IaC generation.
            </p>

            {/* Quick Action Navigation & Guide Toggles */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <button
                onClick={() => setShowGuide(!showGuide)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: showGuide ? 'rgba(0, 210, 211, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                  border: `1px solid ${showGuide ? '#00d2d3' : 'rgba(0, 210, 211, 0.3)'}`,
                  color: showGuide ? '#00d2d3' : '#e2e8f0',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <FaBookOpen size={12} />
                <span>{showGuide ? 'Hide User Guide' : 'How to Use Guide'}</span>
                {showGuide ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>

              <button
                onClick={() => setShowBestPractices(!showBestPractices)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: showBestPractices ? 'rgba(248, 210, 16, 0.2)' : 'rgba(15, 23, 42, 0.7)',
                  border: `1px solid ${showBestPractices ? '#f8d210' : 'rgba(248, 210, 16, 0.3)'}`,
                  color: showBestPractices ? '#f8d210' : '#e2e8f0',
                  padding: '6px 14px',
                  borderRadius: '20px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                <FaLightbulb size={12} color="#f8d210" />
                <span>{showBestPractices ? 'Hide Architecture Tips' : 'Cloud Architecture Pro Tips'}</span>
                {showBestPractices ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
              </button>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '0.78rem', color: '#64748b' }}>
                <span>Jump to:</span>
                <span onClick={() => setActiveTab('sizer')} style={{ color: activeTab === 'sizer' ? '#00d2d3' : '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Sizer</span>
                <span>•</span>
                <span onClick={() => setActiveTab('finops')} style={{ color: activeTab === 'finops' ? '#00d2d3' : '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>FinOps</span>
                <span>•</span>
                <span onClick={() => setActiveTab('topology')} style={{ color: activeTab === 'topology' ? '#00d2d3' : '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Topology</span>
                <span>•</span>
                <span onClick={() => setActiveTab('terraform')} style={{ color: activeTab === 'terraform' ? '#00d2d3' : '#94a3b8', cursor: 'pointer', textDecoration: 'underline' }}>Terraform</span>
              </div>
            </div>
          </div>

          {/* Sizing KPI Block */}
          <div style={{
            background: 'rgba(6, 18, 36, 0.9)',
            border: '1px solid rgba(0, 210, 211, 0.4)',
            borderRadius: '12px',
            padding: '16px 24px',
            textAlign: 'right',
            minWidth: '220px'
          }}>
            <div style={{ fontSize: '0.75rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, letterSpacing: '0.5px' }}>
              Estimated Monthly Spend
            </div>
            <div style={{ fontSize: '2rem', color: '#00d2d3', fontWeight: 800, fontFamily: 'monospace', margin: '4px 0' }}>
              {formatCost(costReport.totalMonthlyCost)}
              <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 400 }}> /mo</span>
            </div>
            <div style={{ fontSize: '0.8rem', color: '#2ecc71', fontWeight: 600 }}>
              {formatCost(costReport.totalAnnualCost)} / year annualized
            </div>
          </div>
        </div>

        {/* INTERACTIVE HOW TO USE GUIDE (COLLAPSIBLE) */}
        {showGuide && (
          <div style={{
            background: 'rgba(8, 20, 39, 0.85)',
            border: '1px solid rgba(0, 210, 211, 0.3)',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '26px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: '10px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(0, 210, 211, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#00d2d3' }}>
                  <FaBookOpen size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                    How to Use This Cloud Architecture & Sizing Suite
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                    Follow this 4-step workflow to size your cloud footprint, optimize commitments, and generate Terraform IaC.
                  </p>
                </div>
              </div>
              <span style={{ fontSize: '0.75rem', background: 'rgba(0, 210, 211, 0.15)', color: '#00d2d3', padding: '3px 10px', borderRadius: '12px', fontWeight: 700 }}>
                Interactive Workflow
              </span>
            </div>

            {/* 4 Step Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '14px', marginBottom: '16px' }}>
              
              {/* Step 1 */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#00d2d3', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>1</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#00d2d3' }}>Pick Archetype or Region</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  Select an instant architectural preset below (e.g. <em>Lean Startup MVP</em> or <em>Enterprise HA</em>) to preload realistic configurations, or switch AWS regions and currency at the top right.
                </p>
              </div>

              {/* Step 2 */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#a29bfe', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>2</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#a29bfe' }}>Fine-Tune Sizing & Storage</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  In the <strong>Architecture Sizer & BOM</strong> tab, adjust EC2 instance classes (Graviton vs x86), node counts, EBS disk type (<code style={{ color: '#00d2d3' }}>gp3</code> vs <code style={{ color: '#00d2d3' }}>io2</code>), S3 tiers, and Multi-AZ RDS database redundancy.
                </p>
              </div>

              {/* Step 3 */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#f8d210', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>3</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f8d210' }}>Evaluate FinOps Savings</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  Navigate to the <strong>FinOps & Savings Plans</strong> tab to model 1-Year (36% off) and 3-Year (62% off) commitments, while auditing automated anomaly alerts for NAT data transfer and cross-AZ egress traps.
                </p>
              </div>

              {/* Step 4 */}
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', border: '1px solid rgba(148, 163, 184, 0.15)', borderRadius: '10px', padding: '14px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ width: '22px', height: '22px', borderRadius: '50%', background: '#2ecc71', color: '#040d1a', fontSize: '0.75rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>4</span>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#2ecc71' }}>Simulate & Export Terraform</span>
                </div>
                <p style={{ fontSize: '0.78rem', color: '#94a3b8', margin: 0, lineHeight: '1.4' }}>
                  Test traffic spikes (1x to 10x) on the <strong>Topology & Burst</strong> tab, then switch to the <strong>Terraform IaC Generator</strong> tab to copy or download ready-to-deploy <code style={{ color: '#2ecc71' }}>.tf</code> infrastructure code.
                </p>
              </div>

            </div>

            {/* Pro Tip Banner */}
            <div style={{
              background: 'rgba(0, 210, 211, 0.08)',
              borderLeft: '4px solid #00d2d3',
              borderRadius: '0 8px 8px 0',
              padding: '10px 14px',
              display: 'flex',
              alignItems: 'center',
              gap: '10px',
              fontSize: '0.78rem',
              color: '#cbd5e1'
            }}>
              <FaInfoCircle color="#00d2d3" size={16} style={{ flexShrink: 0 }} />
              <span>
                <strong>FinOps Best Practice:</strong> Defaulting to AWS Graviton ARM64 instances (<code style={{ color: '#00d2d3' }}>t4g</code>, <code style={{ color: '#00d2d3' }}>m6g</code>) delivers up to <strong>20% better performance at 10-20% lower cost</strong> compared to legacy x86. Always enable free AWS VPC Gateway Endpoints for S3 to eliminate NAT Gateway transfer surcharges ($0.045/GB).
              </span>
            </div>
          </div>
        )}

        {/* ARCHITECTURE BEST PRACTICES (COLLAPSIBLE) */}
        {showBestPractices && (
          <div style={{
            background: 'rgba(8, 20, 39, 0.85)',
            border: '1px solid rgba(248, 210, 16, 0.3)',
            borderRadius: '14px',
            padding: '20px 24px',
            marginBottom: '26px',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.35)',
            animation: 'fadeIn 0.3s ease-in-out'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div style={{ width: '32px', height: '32px', borderRadius: '8px', background: 'rgba(248, 210, 16, 0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#f8d210' }}>
                  <FaLightbulb size={16} />
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', color: '#f8fafc', fontWeight: 700 }}>
                    Cloud Architecture & FinOps Engineering Principles
                  </h3>
                  <p style={{ margin: 0, fontSize: '0.78rem', color: '#94a3b8' }}>
                    Key design considerations aligned with AWS Well-Architected Framework (Reliability & Cost Optimization Pillars).
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowBestPractices(false)}
                style={{ background: 'transparent', border: 'none', color: '#94a3b8', cursor: 'pointer', fontSize: '0.8rem' }}
              >
                ✕ Close
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '14px' }}>
              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  🛡️ Multi-AZ Fault Tolerance
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Deploying EC2 across at least two Availability Zones behind an Application Load Balancer, paired with Multi-AZ synchronous RDS standby replication, eliminates single points of failure and achieves 99.99% availability.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  📦 Storage Tier Lifecycle Management
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Transitioning cold or backup data from S3 Standard ($0.023/GB) to S3 Infrequent Access ($0.0125/GB) and Glacier Flexible Archive ($0.0036/GB) reduces object storage costs by up to 84%.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  ⚠️ NAT Gateway & Egress Surcharges
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Each VPC NAT Gateway costs ~$32.40/month plus $0.045/GB processed. In non-production or staging environments, consolidate into a single shared NAT Gateway to save over $380/year per redundant gateway.
                </div>
              </div>

              <div style={{ background: 'rgba(15, 23, 42, 0.7)', borderRadius: '10px', padding: '14px', border: '1px solid rgba(248, 210, 16, 0.15)' }}>
                <div style={{ color: '#f8d210', fontWeight: 700, fontSize: '0.85rem', marginBottom: '4px' }}>
                  🛠️ Reproducible Infrastructure as Code (IaC)
                </div>
                <div style={{ fontSize: '0.78rem', color: '#94a3b8', lineHeight: '1.4' }}>
                  Avoid manual AWS Management Console clicks by defining VPCs, Security Groups, and Auto-Scaling groups in Terraform HCL. This prevents configuration drift and allows automated CI/CD deployment.
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 1-Click Architectural Presets Bar */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ fontSize: '0.8rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '10px', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <FaLightbulb color="#f8d210" size={13} />
            <span>Architecture Archetype Presets</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
            {ARCHITECTURE_PRESETS.map(preset => {
              const isSelected = activePresetId === preset.id;
              return (
                <div
                  key={preset.id}
                  onClick={() => handleApplyPreset(preset)}
                  style={{
                    backgroundColor: isSelected ? 'rgba(0, 210, 211, 0.12)' : 'rgba(8, 20, 39, 0.65)',
                    border: `1px solid ${isSelected ? '#00d2d3' : 'rgba(148, 163, 184, 0.15)'}`,
                    borderRadius: '10px',
                    padding: '12px 14px',
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    position: 'relative'
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                    <span style={{ fontSize: '0.88rem', fontWeight: 700, color: isSelected ? '#00d2d3' : '#f8fafc' }}>
                      {preset.name}
                    </span>
                    <span style={{
                      fontSize: '0.68rem',
                      background: isSelected ? '#00d2d3' : 'rgba(148, 163, 184, 0.15)',
                      color: isSelected ? '#040d1a' : '#94a3b8',
                      fontWeight: 700,
                      padding: '2px 6px',
                      borderRadius: '4px'
                    }}>
                      {preset.badge}
                    </span>
                  </div>
                  <p style={{ fontSize: '0.76rem', color: '#94a3b8', margin: 0, lineHeight: '1.35' }}>
                    {preset.description}
                  </p>
                </div>
              );
            })}
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{
          display: 'flex',
          gap: '8px',
          borderBottom: '1px solid rgba(148, 163, 184, 0.2)',
          marginBottom: '24px',
          overflowX: 'auto',
          paddingBottom: '2px'
        }}>
          {tabs.map(tab => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: isActive ? 'rgba(0, 210, 211, 0.15)' : 'transparent',
                  color: isActive ? '#00d2d3' : '#94a3b8',
                  border: 'none',
                  borderBottom: `2px solid ${isActive ? '#00d2d3' : 'transparent'}`,
                  padding: '10px 18px',
                  fontSize: '0.9rem',
                  fontWeight: 600,
                  cursor: 'pointer',
                  borderRadius: '6px 6px 0 0',
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s'
                }}
              >
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
              </button>
            );
          })}
        </div>

        {/* TAB 1: ARCHITECTURE SIZER & BILL OF MATERIALS */}
        {activeTab === 'sizer' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '24px' }}>
            
            {/* Left Column: Sizing Controls */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* COMPUTE (EC2) */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#00d2d3', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaServer />
                  <span>Compute: Amazon EC2 Workloads</span>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Instance Family & Sizing</label>
                    <select
                      value={config.ec2.instanceId}
                      onChange={(e) => updateNested('ec2', 'instanceId', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      {EC2_INSTANCES.map(inst => (
                        <option key={inst.id} value={inst.id}>
                          {inst.id} ({inst.vcpu} vCPU, {inst.ram}GB RAM) - ${inst.hourlyRate}/hr
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Instance Count (Auto-Scaling Desired)</label>
                    <input
                      type="number"
                      min="1"
                      max="64"
                      value={config.ec2.quantity}
                      onChange={(e) => updateNested('ec2', 'quantity', Math.max(1, parseInt(e.target.value) || 1))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>EBS Root & Data Disk (GB per instance)</label>
                    <input
                      type="number"
                      min="10"
                      max="4000"
                      value={config.ec2.ebsSizeGb}
                      onChange={(e) => updateNested('ec2', 'ebsSizeGb', Math.max(10, parseInt(e.target.value) || 30))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>EBS Volume Type</label>
                    <select
                      value={config.ec2.ebsType}
                      onChange={(e) => updateNested('ec2', 'ebsType', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      <option value="gp3">gp3 (General Purpose SSD)</option>
                      <option value="io2">io2 (Provisioned IOPS SSD)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* DATABASE (RDS) */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#a29bfe', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaDatabase />
                  <span>Database: Amazon RDS & Aurora</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Database Engine</label>
                    <select
                      value={config.rds.engine}
                      onChange={(e) => updateNested('rds', 'engine', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      {RDS_ENGINES.map(eng => (
                        <option key={eng.id} value={eng.id}>{eng.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>DB Class</label>
                    <select
                      value={config.rds.instanceClass}
                      onChange={(e) => updateNested('rds', 'instanceClass', e.target.value)}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    >
                      {RDS_INSTANCE_CLASSES.map(cls => (
                        <option key={cls.id} value={cls.id}>
                          {cls.id} ({cls.vcpu} vCPU, {cls.ram}GB)
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', alignItems: 'center' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Allocated Storage (GB)</label>
                    <input
                      type="number"
                      min="20"
                      max="10000"
                      value={config.rds.storageGb}
                      onChange={(e) => updateNested('rds', 'storageGb', Math.max(20, parseInt(e.target.value) || 20))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.82rem', color: '#e2e8f0', cursor: 'pointer', marginTop: '14px' }}>
                      <input
                        type="checkbox"
                        checked={config.rds.multiAz}
                        onChange={(e) => updateNested('rds', 'multiAz', e.target.checked)}
                        style={{ width: '18px', height: '18px', accentColor: '#00d2d3' }}
                      />
                      <span>Multi-AZ Standby Deployment (2x Redundancy)</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* STORAGE (S3) */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#f8d210', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaHdd />
                  <span>Storage: Amazon S3 Buckets</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>S3 Standard (GB)</label>
                    <input
                      type="number"
                      min="0"
                      value={config.s3.standardGb}
                      onChange={(e) => updateNested('s3', 'standardGb', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Infrequent Access (GB)</label>
                    <input
                      type="number"
                      min="0"
                      value={config.s3.iaGb}
                      onChange={(e) => updateNested('s3', 'iaGb', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Glacier Archive (GB)</label>
                    <input
                      type="number"
                      min="0"
                      value={config.s3.glacierGb}
                      onChange={(e) => updateNested('s3', 'glacierGb', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              </div>

              {/* NETWORKING & VPC */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#2ecc71', fontWeight: 700, fontSize: '0.95rem', marginBottom: '14px' }}>
                  <FaNetworkWired />
                  <span>Networking: VPC NAT Gateways, ALB & Data Egress</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>NAT Gateways Provisioned</label>
                    <input
                      type="number"
                      min="0"
                      max="8"
                      value={config.networking.natGatewayCount}
                      onChange={(e) => updateNested('networking', 'natGatewayCount', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Data Processed via NAT (GB/mo)</label>
                    <input
                      type="number"
                      min="0"
                      value={config.networking.natDataGb}
                      onChange={(e) => updateNested('networking', 'natDataGb', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Application Load Balancers (ALB)</label>
                    <input
                      type="number"
                      min="0"
                      max="10"
                      value={config.networking.albCount}
                      onChange={(e) => updateNested('networking', 'albCount', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '0.78rem', color: '#94a3b8', marginBottom: '6px' }}>Internet Outbound Egress (GB/mo)</label>
                    <input
                      type="number"
                      min="0"
                      value={config.networking.internetEgressGb}
                      onChange={(e) => updateNested('networking', 'internetEgressGb', Math.max(0, parseInt(e.target.value) || 0))}
                      style={{ width: '100%', background: '#0a192f', color: '#fff', border: '1px solid #1e293b', borderRadius: '6px', padding: '8px', fontSize: '0.85rem' }}
                    />
                  </div>
                </div>
              </div>

            </div>

            {/* Right Column: Sizing Distribution Bar & Itemized Bill of Materials (BOM) */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              
              {/* Category Breakdown Bar */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
                  <span style={{ fontSize: '0.85rem', fontWeight: 700, color: '#f8fafc', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <FaChartPie color="#00d2d3" />
                    <span>Cost Distribution by Architectural Domain</span>
                  </span>
                  <span style={{ fontSize: '0.8rem', color: '#94a3b8' }}>
                    100% of Active Sizing
                  </span>
                </div>

                {/* Multi-segment Progress Bar */}
                <div style={{ display: 'flex', height: '14px', borderRadius: '7px', overflow: 'hidden', backgroundColor: 'rgba(15, 23, 42, 0.8)', marginBottom: '14px' }}>
                  <div style={{ width: `${(costReport.categoryCosts.compute / costReport.totalMonthlyCost) * 100}%`, backgroundColor: '#00d2d3' }} title="Compute" />
                  <div style={{ width: `${(costReport.categoryCosts.database / costReport.totalMonthlyCost) * 100}%`, backgroundColor: '#a29bfe' }} title="Database" />
                  <div style={{ width: `${(costReport.categoryCosts.storage / costReport.totalMonthlyCost) * 100}%`, backgroundColor: '#f8d210' }} title="Storage" />
                  <div style={{ width: `${(costReport.categoryCosts.networking / costReport.totalMonthlyCost) * 100}%`, backgroundColor: '#2ecc71' }} title="Networking" />
                </div>

                {/* Legend */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '10px', fontSize: '0.78rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#00d2d3' }}></span>
                      <span>Compute</span>
                    </span>
                    <strong style={{ color: '#00d2d3' }}>{formatCost(costReport.categoryCosts.compute)}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#a29bfe' }}></span>
                      <span>Database</span>
                    </span>
                    <strong style={{ color: '#a29bfe' }}>{formatCost(costReport.categoryCosts.database)}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#f8d210' }}></span>
                      <span>Storage</span>
                    </span>
                    <strong style={{ color: '#f8d210' }}>{formatCost(costReport.categoryCosts.storage)}</strong>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      <span style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#2ecc71' }}></span>
                      <span>Networking</span>
                    </span>
                    <strong style={{ color: '#2ecc71' }}>{formatCost(costReport.categoryCosts.networking)}</strong>
                  </div>
                </div>
              </div>

              {/* Itemized Bill of Materials (BOM) Table */}
              <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '18px', flex: 1 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' }}>
                  <h3 style={{ fontSize: '0.95rem', fontWeight: 700, margin: 0, color: '#f8fafc' }}>
                    Itemized Bill of Materials (BOM)
                  </h3>
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Monthly AWS Invoice Sizing
                  </span>
                </div>

                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                    <thead>
                      <tr style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.2)', textAlign: 'left', color: '#94a3b8' }}>
                        <th style={{ padding: '8px 6px' }}>Resource Component</th>
                        <th style={{ padding: '8px 6px' }}>Domain</th>
                        <th style={{ padding: '8px 6px', textAlign: 'right' }}>Est. Cost</th>
                        <th style={{ padding: '8px 6px', textAlign: 'right' }}>Share</th>
                      </tr>
                    </thead>
                    <tbody>
                      {costReport.bom.map(item => (
                        <tr key={item.id} style={{ borderBottom: '1px solid rgba(148, 163, 184, 0.08)' }}>
                          <td style={{ padding: '10px 6px' }}>
                            <div style={{ fontWeight: 600, color: '#f8fafc' }}>{item.item}</div>
                            <div style={{ fontSize: '0.72rem', color: '#64748b' }}>{item.detail}</div>
                          </td>
                          <td style={{ padding: '10px 6px' }}>
                            <span style={{
                              fontSize: '0.7rem',
                              padding: '2px 6px',
                              borderRadius: '4px',
                              backgroundColor: item.category === 'Compute' ? 'rgba(0, 210, 211, 0.15)' :
                                               item.category === 'Database' ? 'rgba(162, 155, 254, 0.15)' :
                                               item.category === 'Storage' ? 'rgba(248, 210, 16, 0.15)' : 'rgba(46, 204, 113, 0.15)',
                              color: item.category === 'Compute' ? '#00d2d3' :
                                     item.category === 'Database' ? '#a29bfe' :
                                     item.category === 'Storage' ? '#f8d210' : '#2ecc71',
                              fontWeight: 600
                            }}>
                              {item.category}
                            </span>
                          </td>
                          <td style={{ padding: '10px 6px', textAlign: 'right', fontWeight: 700, fontFamily: 'monospace', color: '#e2e8f0' }}>
                            {formatCost(item.monthlyCost)}
                          </td>
                          <td style={{ padding: '10px 6px', textAlign: 'right', color: '#94a3b8', fontSize: '0.78rem' }}>
                            {item.percentOfTotal.toFixed(1)}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr style={{ borderTop: '2px solid rgba(0, 210, 211, 0.4)' }}>
                        <td colSpan="2" style={{ padding: '12px 6px', fontWeight: 700, color: '#ffffff' }}>Total Architecture Budget</td>
                        <td style={{ padding: '12px 6px', textAlign: 'right', fontWeight: 800, color: '#00d2d3', fontSize: '1rem', fontFamily: 'monospace' }}>
                          {formatCost(costReport.totalMonthlyCost)}
                        </td>
                        <td style={{ padding: '12px 6px', textAlign: 'right', color: '#00d2d3', fontWeight: 700 }}>100%</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>

              </div>

            </div>

          </div>
        )}

        {/* TAB 2: FINOPS & COMMITMENT OPTIMIZATION */}
        {activeTab === 'finops' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Header Banner */}
            <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '20px' }}>
              <h2 style={{ fontSize: '1.25rem', color: '#f8fafc', margin: '0 0 8px 0', fontWeight: 700 }}>
                AWS Commitment Discount Optimizer & FinOps Advisory
              </h2>
              <p style={{ fontSize: '0.88rem', color: '#94a3b8', margin: 0, lineHeight: '1.5' }}>
                Analyze potential cost reduction through AWS Compute Savings Plans and Reserved Instances (RIs) compared to baseline On-Demand commitments.
              </p>
            </div>

            {/* 3 Tier Commitment Cards */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '16px' }}>
              {Object.entries(FINOPS_SAVINGS_RATIOS).map(([key, plan]) => {
                const isSelected = config.commitmentPlan === key;
                const simulatedConfig = { ...config, commitmentPlan: key };
                const simReport = calculateCloudCosts(simulatedConfig);
                const discountPercent = Math.round(plan.discount * 100);

                return (
                  <div
                    key={key}
                    onClick={() => setConfig(prev => ({ ...prev, commitmentPlan: key }))}
                    style={{
                      backgroundColor: isSelected ? 'rgba(0, 210, 211, 0.12)' : 'rgba(8, 20, 39, 0.7)',
                      border: `2px solid ${isSelected ? '#00d2d3' : 'rgba(148, 163, 184, 0.15)'}`,
                      borderRadius: '12px',
                      padding: '20px',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      position: 'relative'
                    }}
                  >
                    {discountPercent > 0 && (
                      <span style={{
                        position: 'absolute',
                        top: '12px',
                        right: '12px',
                        background: '#2ecc71',
                        color: '#040d1a',
                        fontWeight: 800,
                        fontSize: '0.72rem',
                        padding: '3px 8px',
                        borderRadius: '12px'
                      }}>
                        Save {discountPercent}% on EC2
                      </span>
                    )}

                    <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f8fafc', marginBottom: '6px' }}>
                      {key === 'on_demand' ? 'On-Demand Capacity' : key === 'savings_plan_1yr' ? '1-Year Savings Plan' : '3-Year Savings Plan'}
                    </div>

                    <div style={{ fontSize: '0.78rem', color: '#94a3b8', marginBottom: '16px' }}>
                      Commitment Duration: <strong>{plan.commitment}</strong>
                    </div>

                    <div style={{ fontSize: '1.75rem', fontWeight: 800, fontFamily: 'monospace', color: isSelected ? '#00d2d3' : '#f8fafc', marginBottom: '4px' }}>
                      {formatCost(simReport.totalMonthlyCost)}
                      <span style={{ fontSize: '0.85rem', color: '#64748b', fontWeight: 400 }}> /mo</span>
                    </div>

                    <div style={{ fontSize: '0.8rem', color: '#94a3b8', marginBottom: '14px' }}>
                      Annual: {formatCost(simReport.totalAnnualCost)}
                    </div>

                    {discountPercent > 0 ? (
                      <div style={{ background: 'rgba(46, 204, 113, 0.15)', border: '1px solid rgba(46, 204, 113, 0.3)', borderRadius: '6px', padding: '8px 10px', fontSize: '0.76rem', color: '#2ecc71', fontWeight: 600 }}>
                        ✓ Cuts annual compute budget by {formatCost(simReport.annualFinopsSavings)}
                      </div>
                    ) : (
                      <div style={{ background: 'rgba(148, 163, 184, 0.1)', borderRadius: '6px', padding: '8px 10px', fontSize: '0.76rem', color: '#94a3b8' }}>
                        Maximum elasticity, no upfront commitment
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* FinOps High-Risk Gotcha & Anomaly Warnings */}
            <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '14px' }}>
                <FaExclamationTriangle color="#f8d210" size={18} />
                <h3 style={{ fontSize: '1rem', fontWeight: 700, margin: 0, color: '#f8fafc' }}>
                  FinOps Architectural Anomaly Flags & Cost Leaks
                </h3>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                {costReport.finopsRisks.length > 0 ? (
                  costReport.finopsRisks.map((risk, idx) => (
                    <div
                      key={idx}
                      style={{
                        background: risk.level === 'warning' ? 'rgba(235, 77, 75, 0.1)' :
                                    risk.level === 'caution' ? 'rgba(248, 210, 16, 0.1)' : 'rgba(0, 210, 211, 0.1)',
                        borderLeft: `4px solid ${risk.level === 'warning' ? '#ff4757' : risk.level === 'caution' ? '#f8d210' : '#00d2d3'}`,
                        borderRadius: '0 8px 8px 0',
                        padding: '12px 16px'
                      }}
                    >
                      <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#f8fafc', marginBottom: '4px' }}>
                        {risk.title}
                      </div>
                      <div style={{ fontSize: '0.8rem', color: '#94a3b8', lineHeight: '1.4' }}>
                        {risk.desc}
                      </div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#2ecc71', fontSize: '0.85rem' }}>
                    ✓ No high-risk architectural cost anomalies detected with current parameters.
                  </div>
                )}
              </div>
            </div>

          </div>
        )}

        {/* TAB 3: TOPOLOGY & TRAFFIC BURST SIMULATOR */}
        {activeTab === 'topology' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
            
            {/* Burst Simulation Slider Control */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(8, 24, 48, 0.85) 0%, rgba(4, 15, 30, 0.95) 100%)',
              border: '1px solid rgba(0, 210, 211, 0.35)',
              borderRadius: '12px',
              padding: '20px 24px'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '10px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <FaBolt color="#f8d210" size={18} />
                  <span style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f8fafc' }}>
                    Monthly Traffic Surge & Black Friday Burst Simulator
                  </span>
                </div>
                <div style={{ fontSize: '1rem', fontWeight: 800, color: config.burstMultiplier > 1.0 ? '#f8d210' : '#00d2d3', fontFamily: 'monospace' }}>
                  {config.burstMultiplier.toFixed(1)}x Traffic Surge Multiplier
                </div>
              </div>

              <p style={{ fontSize: '0.82rem', color: '#94a3b8', margin: '0 0 16px 0' }}>
                Simulate how sudden inbound request volume and data egress scale autoscaled EC2 instance counts, ALB LCUs, and egress bandwidth elasticity.
              </p>

              <input
                type="range"
                min="1.0"
                max="10.0"
                step="0.5"
                value={config.burstMultiplier}
                onChange={(e) => setConfig(prev => ({ ...prev, burstMultiplier: parseFloat(e.target.value) }))}
                style={{ width: '100%', accentColor: '#00d2d3', cursor: 'pointer', marginBottom: '10px' }}
              />

              <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.75rem', color: '#64748b' }}>
                <span>1.0x (Normal Baseline)</span>
                <span>2.5x (Peak Day)</span>
                <span>5.0x (Product Launch)</span>
                <span>10.0x (Black Friday Surge)</span>
              </div>
            </div>

            {/* Visual Architecture Topology Diagram */}
            <div style={{ background: 'rgba(8, 20, 39, 0.7)', border: '1px solid rgba(0, 210, 211, 0.25)', borderRadius: '12px', padding: '24px' }}>
              <h3 style={{ fontSize: '1.05rem', fontWeight: 700, color: '#f8fafc', margin: '0 0 20px 0', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <FaNetworkWired color="#00d2d3" />
                <span>Multi-Tier High-Availability Topology Diagram</span>
              </h3>

              {/* 4 Interactive Architectural Tiers */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                
                {/* TIER 1: Edge & Ingress */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px dashed rgba(148, 163, 184, 0.3)', borderRadius: '10px', padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#94a3b8', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>
                    Tier 1: Global Edge & Internet Gateway
                  </div>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(69, 170, 242, 0.15)', border: '1px solid #45aaf2', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600 }}>
                      Clients / Public Users (HTTPS :443)
                    </div>
                    <div style={{ color: '#64748b', display: 'flex', alignItems: 'center' }}>➔</div>
                    <div style={{ background: 'rgba(69, 170, 242, 0.15)', border: '1px solid #45aaf2', borderRadius: '8px', padding: '8px 14px', fontSize: '0.82rem', fontWeight: 600 }}>
                      AWS Internet Gateway (IGW)
                    </div>
                  </div>
                </div>

                {/* TIER 2: Public Subnet */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(0, 210, 211, 0.3)', borderRadius: '10px', padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#00d2d3', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>
                    Tier 2: Public Subnets (Multi-AZ DMZ)
                  </div>
                  <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                    <div style={{ background: 'rgba(0, 210, 211, 0.15)', border: '1px solid #00d2d3', borderRadius: '8px', padding: '10px 14px', flex: 1, minWidth: '200px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#00d2d3' }}>Application Load Balancer</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {config.networking.albCount}x ALB ({costReport.effectiveEc2Qty > 1 ? 'Round-Robin SSL Offload' : 'Direct Target'})
                      </div>
                    </div>
                    <div style={{ background: 'rgba(0, 210, 211, 0.15)', border: '1px solid #00d2d3', borderRadius: '8px', padding: '10px 14px', flex: 1, minWidth: '200px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#00d2d3' }}>VPC NAT Gateway</div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {config.networking.natGatewayCount}x NAT Gateways with Elastic IPs for Outbound
                      </div>
                    </div>
                  </div>
                </div>

                {/* TIER 3: Private App Tier */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(162, 155, 254, 0.3)', borderRadius: '10px', padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#a29bfe', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>
                    Tier 3: Private Application Subnets (Auto Scaling EC2)
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    <div style={{ background: 'rgba(162, 155, 254, 0.15)', border: '1px solid #a29bfe', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#a29bfe' }}>
                        {costReport.effectiveEc2Qty}x {config.ec2.instanceId} Instances
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {costReport.instanceObj.vcpu} vCPUs, {costReport.instanceObj.ram} GB RAM / node
                      </div>
                    </div>
                    <div style={{ background: 'rgba(162, 155, 254, 0.15)', border: '1px solid #a29bfe', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#a29bfe' }}>
                        EBS {config.ec2.ebsType ? config.ec2.ebsType.toUpperCase() : 'GP3'} Storage
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {costReport.effectiveEc2Qty * config.ec2.ebsSizeGb} GB provisioned block storage
                      </div>
                    </div>
                  </div>
                </div>

                {/* TIER 4: Isolated Database & Storage */}
                <div style={{ background: 'rgba(15, 23, 42, 0.8)', border: '1px solid rgba(46, 204, 113, 0.3)', borderRadius: '10px', padding: '14px 18px' }}>
                  <div style={{ fontSize: '0.72rem', color: '#2ecc71', textTransform: 'uppercase', fontWeight: 700, marginBottom: '8px' }}>
                    Tier 4: Isolated Database & Storage Tier
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                    <div style={{ background: 'rgba(46, 204, 113, 0.15)', border: '1px solid #2ecc71', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#2ecc71' }}>
                        Amazon RDS ({costReport.rdsEngineObj.name})
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {config.rds.multiAz ? 'Multi-AZ Active/Standby Failover' : 'Single-AZ Standalone'} | {config.rds.storageGb} GB
                      </div>
                    </div>
                    <div style={{ background: 'rgba(46, 204, 113, 0.15)', border: '1px solid #2ecc71', borderRadius: '8px', padding: '10px 14px' }}>
                      <div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#2ecc71' }}>
                        Amazon S3 Object Storage
                      </div>
                      <div style={{ fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                        {(config.s3.standardGb + config.s3.iaGb + config.s3.glacierGb).toLocaleString()} GB provisioned with AES-256
                      </div>
                    </div>
                  </div>
                </div>

              </div>

            </div>

          </div>
        )}

        {/* TAB 4: TERRAFORM IAC GENERATOR */}
        {activeTab === 'terraform' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{
              background: 'rgba(8, 20, 39, 0.7)',
              border: '1px solid rgba(0, 210, 211, 0.25)',
              borderRadius: '12px',
              padding: '20px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '14px'
            }}>
              <div>
                <h2 style={{ fontSize: '1.25rem', color: '#f8fafc', margin: '0 0 6px 0', fontWeight: 700 }}>
                  Automated Terraform HCL Infrastructure as Code
                </h2>
                <p style={{ fontSize: '0.85rem', color: '#94a3b8', margin: 0 }}>
                  Production-ready, modular Terraform configuration reflecting your active sizing, subnets, security groups, ALB, ASG, and Multi-AZ RDS.
                </p>
              </div>

              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  onClick={handleCopyTerraform}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: copiedCode ? '#2ecc71' : '#00d2d3',
                    color: '#040d1a',
                    border: 'none',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  {copiedCode ? <FaCheck /> : <FaCopy />}
                  <span>{copiedCode ? 'Copied to Clipboard!' : 'Copy Terraform HCL'}</span>
                </button>

                <button
                  onClick={handleDownloadTerraform}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    backgroundColor: 'rgba(148, 163, 184, 0.15)',
                    color: '#f8fafc',
                    border: '1px solid rgba(148, 163, 184, 0.3)',
                    borderRadius: '8px',
                    padding: '8px 16px',
                    fontSize: '0.85rem',
                    fontWeight: 600,
                    cursor: 'pointer'
                  }}
                >
                  <FaDownload />
                  <span>Download .tf File</span>
                </button>
              </div>
            </div>

            {/* Code Block Container */}
            <div style={{
              background: '#0a192f',
              border: '1px solid rgba(0, 210, 211, 0.3)',
              borderRadius: '12px',
              padding: '20px',
              overflowX: 'auto',
              maxHeight: '650px',
              overflowY: 'auto'
            }}>
              <pre style={{
                margin: 0,
                fontFamily: "'Courier New', Courier, monospace",
                fontSize: '0.84rem',
                lineHeight: '1.5',
                color: '#38ef7d'
              }}>
                {generateTerraformHcl(config)}
              </pre>
            </div>

          </div>
        )}

      </div>
    </div>
  );
};

export default CloudCostEstimatorPage;
